import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StudentNavigation from '../../components/ui/StudentNavigation';
import InstituteNavigation from '../../components/ui/InstituteNavigation';
import ChatHeader from './components/ChatHeader';
import MessageBubble from './components/MessageBubble';
import MessageInput from './components/MessageInput';
import ChatSidebar from './components/ChatSidebar';
import EmptyState from './components/EmptyState';
import { useAuth } from '../../contexts/AuthContext';
import { chatService } from '../../services/chatService';
import { aiChatService } from '../../services/aiChatService';
import { supabase } from '../../lib/supabase';

const ChatInterface = () => {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const { user, userProfile } = useAuth();

  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatEnabled, setChatEnabled] = useState(null);

  // Keep a ref to the active conversation ID so subscription callbacks
  // always see the latest value without needing to re-subscribe.
  const activeConvIdRef = useRef(null);
  activeConvIdRef.current = activeConversationId;

  // ── Chat access check ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!user || !userProfile) return;

    const checkChatAccess = async () => {
      if (userProfile?.role === 'institute') {
        try {
          const { data } = await supabase?.from('institute_profiles')?.select('chat_enabled')?.eq('user_id', user?.id)?.single();
          setChatEnabled(data?.chat_enabled ?? false);
        } catch {
          setChatEnabled(false);
        }
      } else {
        setChatEnabled(true);
      }
    };

    checkChatAccess();
  }, [user, userProfile]);

  // ── Load conversations + subscribe ─────────────────────────────────────────
  const loadConversations = useCallback(async () => {
    if (!user?.id || !userProfile?.role) return;
    try {
      const data = await chatService?.getConversations(user?.id, userProfile?.role);
      setConversations(data);
      // Auto-select first conversation if none selected
      if (data?.length > 0 && !activeConvIdRef?.current) {
        setActiveConversationId(data?.[0]?.id);
      }
    } catch (err) {
      setError(err?.message);
    }
  }, [user?.id, userProfile?.role]);

  useEffect(() => {
    if (!user || !userProfile || chatEnabled === null) return;
    if (!chatEnabled) { setLoading(false); return; }

    loadConversations()?.finally(() => setLoading(false));

    // Subscribe to conversation-level changes so sidebar stays fresh
    const unsubscribe = chatService?.subscribeToConversations(
      user?.id,
      userProfile?.role,
      () => loadConversations()
    );

    return () => unsubscribe();
  }, [user?.id, userProfile?.role, chatEnabled, loadConversations]);

  // ── Load messages + subscribe to real-time ─────────────────────────────────
  useEffect(() => {
    if (!activeConversationId) return;

    setMessages([]);

    // Load existing messages
    chatService?.getMessages(activeConversationId)?.then((data) => setMessages(data || []))?.catch((err) => setError(err?.message));

    // Subscribe to new messages — this is the core real-time piece
    const unsubscribe = chatService?.subscribeToMessages(
      activeConversationId,
      (newMessage) => {
        setMessages((prev) => {
          // Deduplicate by ID
          if (prev?.some((m) => m?.id === newMessage?.id)) return prev;
          return [...prev, newMessage];
        });
      }
    );

    return () => unsubscribe();
  }, [activeConversationId]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send message ───────────────────────────────────────────────────────────
  const handleSendMessage = async (messageText, fileUrl = null) => {
    if (!messageText?.trim() && !fileUrl) return;

    try {
      const conversation = conversations?.find((c) => c?.id === activeConversationId);
      if (!conversation) return;

      const isStudent = userProfile?.role === 'student';

      // Send the message — Realtime subscription delivers it to both sides
      await chatService?.sendMessage(
        activeConversationId,
        user?.id,
        messageText,
        fileUrl ? 'file' : 'text',
        fileUrl
      );

      // If student is chatting with AI handler, generate AI response
      if (isStudent && conversation?.handlerType === 'ai') {
        setIsTyping(true);
        try {
          const aiResponse = await aiChatService?.generateAIResponse(
            activeConversationId,
            messageText,
            conversation?.instituteId
          );

          await chatService?.sendMessage(
            activeConversationId,
            conversation?.instituteId,
            aiResponse?.message,
            'text',
            null,
            aiResponse?.senderType,
            aiResponse?.aiModel,
            aiResponse?.aiConfidence,
            aiResponse?.escalationReason
          );
        } catch (aiError) {
          console.error('AI response error:', aiError);
        } finally {
          setIsTyping(false);
        }
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleTakeover = async () => {
    if (!activeConversationId) return;
    try {
      await aiChatService?.takeoverConversation(activeConversationId);
      await loadConversations();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleVideoCall = () => {
    console.log('Video call initiated');
  };

  const handleBack = () => setIsMobileSidebarOpen(true);

  const handleStartNewChat = () => navigate('/exhibition-hall');

  const activeConversation = conversations?.find((c) => c?.id === activeConversationId);
  const userRole = userProfile?.role || 'student';
  const isInstitute = userRole === 'institute';
  const instituteProfile = userProfile?.institute_profiles?.[0];
  const isInstituteApproved = !isInstitute || instituteProfile?.approval_status === 'approved';

  // ── Loading state ──────────────────────────────────────────────────────────
  if (loading || chatEnabled === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading conversations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-4">{error}</p>
          <button onClick={() => window.location?.reload()} className="text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Chat not activated for this institute
  if (isInstitute && !chatEnabled) {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="pt-[60px] flex items-center justify-center min-h-screen">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Chat Not Activated</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Chat access not activated. Please contact admin.
              </p>
              <button
                onClick={() => navigate('/institute-dashboard')}
                className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isInstituteApproved) {
    return (
      <div className="min-h-screen bg-background">
        <InstituteNavigation />
        <div className="pt-[60px] flex items-center justify-center min-h-screen">
          <div className="max-w-md w-full mx-4 text-center">
            <div className="bg-card rounded-xl shadow-lg p-8 border border-border">
              <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-warning)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="12" y1="8" x2="12" y2="12"/>
                  <line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground mb-2">Access Restricted</h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Your account is pending admin approval. Chat and video counseling will be available once approved.
              </p>
              <button
                onClick={() => navigate('/institute-dashboard')}
                className="mt-6 px-6 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-smooth"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {userRole === 'student' ? <StudentNavigation /> : <InstituteNavigation />}
      <div className="pt-[60px] h-screen flex flex-col">
        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar */}
          <div className={`${isMobileSidebarOpen ? 'block' : 'hidden'} lg:block w-full lg:w-80 xl:w-96 flex-shrink-0`}>
            <ChatSidebar
              conversations={conversations}
              activeConversationId={activeConversationId}
              onSelectConversation={(id) => {
                setActiveConversationId(id);
                setIsMobileSidebarOpen(false);
              }}
              userRole={userRole}
              isMobileOpen={isMobileSidebarOpen}
              onMobileClose={() => setIsMobileSidebarOpen(false)}
            />
          </div>

          {/* Chat area */}
          <div className={`${!isMobileSidebarOpen ? 'block' : 'hidden'} lg:block flex-1 flex flex-col`}>
            {activeConversation ? (
              <>
                <ChatHeader
                  instituteName={
                    userRole === 'student'
                      ? activeConversation?.institute?.name
                      : activeConversation?.student?.name
                  }
                  representativeName={
                    userRole === 'student'
                      ? activeConversation?.institute?.name
                      : activeConversation?.student?.name
                  }
                  isOnline={true}
                  handlerType={activeConversation?.handlerType}
                  onVideoCall={handleVideoCall}
                  onBack={handleBack}
                  onTakeover={userProfile?.role === 'institute' ? handleTakeover : null}
                  instituteAvatar="/assets/images/no_image.png"
                  instituteAvatarAlt="Profile photo"
                />

                <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4 md:py-6 bg-background">
                  <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-6">
                      <p className="text-xs text-muted-foreground">
                        {new Date()?.toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </p>
                    </div>

                    {messages?.map((message) => (
                      <MessageBubble
                        key={message?.id}
                        message={message?.message}
                        senderName={message?.sender?.name || 'Unknown'}
                        senderAvatar={message?.sender?.avatar_url}
                        senderType={message?.senderType}
                        aiModel={message?.aiModel}
                        timestamp={message?.sentAt}
                        isOwn={message?.senderId === user?.id}
                        type={message?.messageType}
                        fileName={message?.fileUrl ? message?.fileUrl?.split('/')?.pop() : null}
                        fileSize={message?.fileUrl ? '2.4 MB' : null}
                      />
                    ))}

                    <div ref={messagesEndRef} />
                  </div>
                </div>

                <MessageInput
                  onSendMessage={handleSendMessage}
                  isTyping={isTyping}
                  disabled={!activeConversationId}
                />
              </>
            ) : (
              <EmptyState userRole={userRole} onStartNewChat={handleStartNewChat} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;